import EventEmitter from 'eventemitter3';
import { useEffect, useRef } from 'react';
import type { AssetItemModel } from '@components/asset/models/AssetItemModel';

const eventEmitter = new EventEmitter();

// The relad grids event is update server side grids when an action is performed in an info card
// Reloading the master grid also automatically reloads any open details grids
export const RELOAD_GRIDS = 'RELOAD_GRIDS';

export const VIEW_RELATIONS_IN_TABLE = 'VIEW_RELATIONS_IN_TABLE';
interface ViewRelationsInTablePayload {
    names: string[];
    ids: string[];
}

// Example code that can be deleted once we have code that serves as usage hint
export const TYPED_PAYLOAD_DEMO = 'TYPED_PAYLOAD_DEMO';
interface TYPED_PAYLOAD_DEMO_PAYLOAD {
    comment: 'This example code will stay commited for now as a demo to how you can get typed payload for your events';
}

export const ASSET_ITEM_UPDATED = 'ASSET_ITEM_UPDATED';
interface AssetItemUpdatedPayload {
    items: AssetItemModel[];
}

export const ASSET_ITEM_DELETED = 'ASSET_ITEM_DELETED';
interface AssetItemDeletedPayload {
    ids: string[];
}

interface EventsDefinition {
    [RELOAD_GRIDS]: never;
    [VIEW_RELATIONS_IN_TABLE]: ViewRelationsInTablePayload;
    [TYPED_PAYLOAD_DEMO]: TYPED_PAYLOAD_DEMO_PAYLOAD;
    [ASSET_ITEM_UPDATED]: AssetItemUpdatedPayload;
    [ASSET_ITEM_DELETED]: AssetItemDeletedPayload;
}

type Events = keyof EventsDefinition;

const Emitter = {
    on: <T extends Events>(
        event: T,
        fn: (paylod: EventsDefinition[T]) => void,
    ) => eventEmitter.on<Events>(event, fn),
    once: <T extends Events>(
        event: T,
        fn: (paylod: EventsDefinition[T]) => void,
    ) => eventEmitter.once<Events>(event, fn),
    off: <T extends Events>(
        event: T,
        fn: (paylod: EventsDefinition[T]) => void,
    ) => eventEmitter.off<Events>(event, fn),
    emit: <T extends Events>(
        event: T,
        ...[payload]: EventsDefinition[T] extends never
            ? []
            : [EventsDefinition[T]]
    ) => eventEmitter.emit<Events>(event, payload),
};

Object.freeze(Emitter);

export const useEventListener = <T extends Events>(
    type: T,
    fn: (payload: EventsDefinition[T]) => void,
) => {
    const fnRef = useRef(fn);
    fnRef.current = fn;

    useEffect(() => {
        const handler = (payload: EventsDefinition[T]) => {
            fnRef.current(payload);
        };
        Emitter.on(type, handler);
        return () => {
            Emitter.off(type, handler);
        };
    }, [type]);
};

export const useEventDispatcher = <T extends Events>(event: T) => {
    return (
        ...payload: EventsDefinition[T] extends never
            ? []
            : [EventsDefinition[T]]
    ) => {
        Emitter.emit(event, ...payload);
    };
};
